/**
 * Static Tag Form Modal
 * Simple form for creating/editing static tags (name + color).
 * For Media Devoted | Created By Ciro Autiero
 */

import { FC, useEffect, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { Tag, X } from 'lucide-react';

import { Button } from '@/components/ui';
import { Card } from '@/components/ui';
import { ColorPicker } from '@/components/ui';
import { Input } from '@/components/ui';
import { TAG_COLOR_PRESETS } from '@/pages/admin/ListTagRulesPage/constants';

interface StaticTagFormModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  initial?: { name: string; color: string } | null;
  onSubmit: (values: { name: string; color: string }) => Promise<void>;
  submitting?: boolean;
}

export const StaticTagFormModal: FC<StaticTagFormModalProps> = ({
  open,
  onOpenChange,
  initial,
  onSubmit,
  submitting,
}) => {
  const [name, setName] = useState('');
  const [color, setColor] = useState('#3b82f6');

  useEffect(() => {
    if (open) {
      setName(initial?.name ?? '');
      setColor(initial?.color ?? '#3b82f6');
    }
  }, [open, initial]);

  const handleSubmit = async () => {
    if (!name.trim()) return;
    await onSubmit({ name: name.trim(), color });
  };

  if (!open) return null;

  return (
    <AnimatePresence>
      <motion.div
        className="fixed inset-0 z-50"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
      >
        <div
          className="absolute inset-0 bg-black/60 backdrop-blur-sm"
          onClick={() => onOpenChange(false)}
          aria-hidden="true"
        />
        <motion.div
          initial={{ opacity: 0, y: 20, scale: 0.98 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          exit={{ opacity: 0, y: 20, scale: 0.98 }}
          transition={{ duration: 0.2 }}
          className="absolute inset-0 flex items-center justify-center p-4"
          role="dialog"
          aria-modal="true"
        >
          <Card className="w-full max-w-md shadow-2xl" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between border-b bg-muted/30 px-6 py-4">
              <div className="flex items-center gap-2">
                <div className="flex h-8 w-8 items-center justify-center rounded-md bg-primary/10 text-primary">
                  <Tag className="h-4 w-4" />
                </div>
                <h2 className="text-lg font-semibold">
                  {initial ? 'Edit Static Tag' : 'Create Static Tag'}
                </h2>
              </div>
              <Button variant="ghost" size="icon" onClick={() => onOpenChange(false)}>
                <X className="h-5 w-5" />
              </Button>
            </div>

            <div className="space-y-5 px-6 py-5">
              <div>
                <label className="block text-sm font-medium">Tag Name</label>
                <Input
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="e.g. VIP, Blacklist"
                  className="mt-2"
                  autoFocus
                />
              </div>

              <div>
                <label className="block text-sm font-medium">Badge Color</label>
                <div className="mt-2 flex items-center gap-3">
                  <ColorPicker
                    color={color}
                    onChange={setColor}
                    presets={TAG_COLOR_PRESETS}
                    className="h-10 w-10 shrink-0 rounded-md border border-input"
                  />
                  <Input
                    value={color}
                    onChange={(e) => setColor(e.target.value)}
                    placeholder="#000000"
                    className="font-mono uppercase"
                  />
                </div>
              </div>
            </div>

            <div className="flex justify-end gap-3 border-t bg-muted/20 px-6 py-4">
              <Button variant="outline" onClick={() => onOpenChange(false)} disabled={submitting}>
                Cancel
              </Button>
              <Button onClick={handleSubmit} disabled={submitting || !name.trim()}>
                {submitting ? 'Saving...' : initial ? 'Save Changes' : 'Create Tag'}
              </Button>
            </div>
          </Card>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};
